import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  TextField, useMediaQuery, useTheme,
} from '@mui/material';
import type { User } from '../../api/types';
import { useUsers } from '../../api/hooks';
import { useErrorMessage } from '../common/Common';

/** The picker only ever offers people the API will accept: the directory is login-seeded, so
 *  "everyone who has signed in at least once" is exactly the assignable set (ADR-69). */
export function OwnerPickerDialog({
  open, currentOwnerUserId, busy = false, error, onSave, onClose,
}: {
  open: boolean;
  currentOwnerUserId: string | null;
  busy?: boolean;
  error?: unknown;
  onSave: (ownerUserId: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const describeError = useErrorMessage();
  const { data: users } = useUsers();

  const [selected, setSelected] = useState<User | null>(null);

  const resolvedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    resolvedFor.current = null;
  }, [open, currentOwnerUserId]);

  // Same reason as the form dialogs: the user list resolves asynchronously, and re-running on
  // every change would reset a selection the user had already made.
  useEffect(() => {
    if (!open || !users) return;
    const key = currentOwnerUserId ?? '';
    if (resolvedFor.current === key) return;
    resolvedFor.current = key;
    setSelected(users.find((u) => u.userId === currentOwnerUserId) ?? null);
  }, [open, currentOwnerUserId, users]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{t('owner.changeOwner')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {Boolean(error) && <Alert severity="error">{describeError(error)}</Alert>}
          <Autocomplete
            options={users ?? []}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            noOptionsText={t('owner.noUsers')}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('owner.owner')}
                placeholder={t('owner.unassigned')}
                helperText={t('owner.hint')}
                inputProps={{ ...params.inputProps, 'data-testid': 'owner-input' }}
              />
            )}
            data-testid="owner-picker"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => onSave(selected?.userId ?? null)}
          data-testid="save-owner"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
